import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, CircularProgress } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import toast from "react-hot-toast";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomButton from "../../../components/CustomButton";
import CustomText, { textStyles } from "../../../components/CustomText";
import { useTranslation } from "react-i18next";

const ApproveMethod = () => {
  const [loading, setLoading] = useState(true);
  const [approvalData, setApprovalData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const isMobile = useMediaQuery("(max-width:768px)");
  const { width, height } = useWindowSize();
 const { t } = useTranslation();
  const upgradeToken = searchParams.get("upgrade_token");
  const decryptedToken = upgradeToken || (token ? decryptToken(token) : null);
  useEffect(() => {
    const approvePaymentMethod = async () => {
      try {
        const subscription_id = searchParams.get("subscription_id");
        const ba_token = searchParams.get("ba_token");

        console.log("🔍 Extracted Params:", { subscription_id, ba_token });

        if (!subscription_id || !ba_token) {
throw new Error(t("alerts.missing_paypal_params"));        }

        const body = { subscription_id, ba_token };

        console.log("📤 Sending request to approve payment method:", body);

        const res = await fetch(`${BASE_URL}/payments/approve-payment-method-update`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
          body: JSON.stringify(body),
        });

        const json = await res.json();
        console.log("📥 Response:", json);

        if (!res.ok || json.error) {
throw new Error(json.message || t("alerts.payment_update_approve_error"));
        }

toast.success(t("alerts.payment_update_approve_success"));
        setApprovalData(json.data);
        setShowConfetti(true);

        // Auto stop confetti after 5s
        setTimeout(() => setShowConfetti(false), 5000);
      } catch (err) {
        console.error("❌ Error approving payment method:", err);
       
          setErrorMsg(err.message || "Failed to approve payment method update");
toast.error(err.message || t("alerts.general_error"));

  // 🔁 Open PayPal autopay page so the user can manually reconnect
  window.open("https://www.paypal.com/myaccount/autopay/", "_blank");
      } finally {
        setLoading(false);
      }
    };

    approvePaymentMethod();
  }, [searchParams, decryptedToken]);

  // ---------------- UI Section ----------------
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
      
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
        }}
      >
        <CancelIcon sx={{ fontSize: 90, color: "#f44336", mb: 2 }} />
        <Typography
          variant="h4"
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: "bold",
            color: "#f44336",
            mb: 1,
          }}
        >
            {t("subscriptionPage.payment_method_update_failed")}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            color: "#5E5C5C",
            mb: 3,
            maxWidth: 500,
          }}
        >
          {errorMsg}
        </Typography>
        <CustomButton
          size="medium"
          label={t("subscriptionPage.back_to_subscription")}
          onClick={() => navigate("/subscription")}
        />
      </Box>
    );
  }

  // ✅ Success screen (styled like RevisedSuccess)
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
        py: 5,
      }}
    >
      {/* 🎊 Confetti */}
      {showConfetti && <Confetti width={width} height={height} />}

      {/* ✅ Icon */}
      <CheckCircleIcon
        sx={{ fontSize: isMobile ? 70 : 90, color: "#4caf50", mb: 2 }}
      />

      {/* Heading */}
      <Typography
        variant="h4"
        sx={{
          fontSize: isMobile ? "35px" : "30px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: "bold",
          mb: 2,
        }}
      >
         {t("subscriptionPage.payment_method_updated_success")}
      </Typography>

      {/* Subtext */}
      <Typography
        variant="body1"
        sx={{
          mb: 4,
          fontFamily: "Poppins, sans-serif",
          color: "#5E5C5C",
          maxWidth: 500,
          fontSize: "16px",
          lineHeight: 1.4,
        }}
      >
         {t("subscriptionPage.payment_method_updated_success_desc")}
      </Typography>

      {/* Info Card */}
      {approvalData && (
        <Box
          sx={{
            bgcolor: "#F7F7F7",
            borderRadius: 2,
            py: 6,
            px: 4,
            mb: 3,
            width: "100%",
            maxWidth: 450,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          {[
            { label: t("subscriptionPage.subscription_id"), value: approvalData.subscription_id },
            { label: t("subscriptionPage.status"), value: approvalData.status || "APPROVED" },
          ].map((row, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: i === 3 ? 0 : 2,
              }}
            >
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 15,
                  fontWeight: 400,
                  color: "#5E5C5C",
                }}
              >
                {row.label}
              </CustomText>
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 15,
                  fontWeight: 400,
                  color: "#172B4D",
                  maxWidth: "55%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={String(row.value)}
              >
                {row.value}
              </CustomText>
            </Box>
          ))}
        </Box>
      )}

      {/* Button */}
      <CustomButton
        size="medium"
        label={t("subscriptionPage.back_to_subscription")}
        onClick={() => navigate(upgradeToken ? `/subscription-plans?upgrade_token=${encodeURIComponent(upgradeToken)}` : "/subscription-plans")}
      />
    </Box>
  );
};

export default ApproveMethod;
